import { extractAccessTokenFromCookies, extractUserIdFromAccessToken } from '@/helpers/utils';
import type { AbstractAdaptor, AnalyticsParams } from '@/services/analytics/adaptors/abstract';

export type ZeblineUserProfileAttributes = {
  firstName: string;
  email: string;
  mobile: string;
  birth_date: Date;
  gender: string;
  job_title: string;
  total_rooms_count: number;
  current_subscription_plan: string; // { title: string; stopTime: Date; unit: string }[]
  profile_completed: boolean;
  push_opt_in: boolean;
  ltv_3month: number;
  ltv_6month: number;
  ltv_1year: number;
  aov: number;
  current_packages: string; // [record, iframe]
  packages_end_date: string; // [{ packageName: 'record' , end_date: new Date }]
};

type ZeblineEventName =
  | 'user_signed_up'
  | 'user_logged_in'
  | 'trial_service'
  | 'button_clicked'
  | 'plan_changed'
  | 'payment_successful'
  | 'payment_failed';

export type ZeblineTrackParams = {
  eventName: ZeblineEventName;
  userId: string;
  eventTime: Date;
  eventData?: AnalyticsParams;
};

interface ZeblineEvent {
  track(eventName: ZeblineEventName, params?: ZeblineTrackParams): void;
}

interface Zebline {
  event: ZeblineEvent;
  initData: {
    licence_code?: string;
    token?: string;
  };
  init(config: {
    token: string;
    licence_code: string;
    notificationRequest?: boolean;
    options?: {
      events?: string[];
    };
  }): void;
  user: {
    login: (userId: string) => void;
    logout: VoidFunction;
    setAttribute: (key: string, value: unknown) => void;
    setAttributes: (value: Partial<ZeblineUserProfileAttributes>) => void;
  };
}

declare global {
  interface Window {
    zebline?: Zebline;
  }
}

/**
 * Zebline Analytics Adaptor
 * The Zebline SDK script should be loaded before using this adaptor
 * SDK: https://sdk.zebline.com/js/v1/sgm-sdk.js
 *
 * The SDK creates `window.zebline` in stages: the object itself may exist
 * before `.event` or `.user` are populated. Every public method here queues
 * its action and retries on a shared poller (max 5 attempts, 50ms apart)
 * until the piece of the SDK it needs is actually present. If the SDK never
 * shows up within those 5 attempts, pending actions are dropped and a
 * warning is logged instead of retrying forever.
 */
export class ZeblineAdaptor implements AbstractAdaptor {
  token: string | undefined;
  licenceCode: string | undefined;

  private pendingEvents: Array<{ name: ZeblineEventName; params?: AnalyticsParams }> = [];
  private pendingUserActions: Array<(zebline: Zebline) => void> = [];
  private sdkWaitTimer?: ReturnType<typeof setInterval>;
  private sdkWaitAttempts = 0;

  private static readonly MAX_SDK_WAIT_ATTEMPTS = 6;
  private static readonly SDK_WAIT_INTERVAL_MS = 50;

  private getAccessToken() {
    const accessToken = extractAccessTokenFromCookies();
    const userId = extractUserIdFromAccessToken(accessToken);

    return userId;
  }

  private prepareSendParams(name: ZeblineEventName, params?: AnalyticsParams): ZeblineTrackParams {
    const accessToken = extractAccessTokenFromCookies();
    const userId = extractUserIdFromAccessToken(accessToken);
    const sendParams: ZeblineTrackParams = {
      eventName: name,
      userId: userId || '',
      eventTime: new Date().toISOString() as unknown as Date,
    };

    if (params) {
      sendParams.eventData = params;
    }

    return sendParams;
  }

  init(options: { token: string; licenceCode: string }) {
    // Avoid re-triggering init if we've already got credentials set,
    // e.g. this can be called multiple times from route/layout watchers.
    if (this.token && this.licenceCode) return;

    this.token = options.token;
    this.licenceCode = options.licenceCode;

    this.ensurePolling();
  }

  send(name: ZeblineEventName, params?: AnalyticsParams) {
    this.pendingEvents.push({ name, params });
    this.ensurePolling();
  }

  login() {
    const userId = this.getAccessToken();
    if (!userId) return;

    this.queueUserAction((zebline) => zebline.user.login(userId));
  }

  logout() {
    this.queueUserAction((zebline) => zebline.user.logout());
  }

  setAttribute<K extends keyof ZeblineUserProfileAttributes>(key: K, value: ZeblineUserProfileAttributes[K]) {
    if (value === undefined || value === null) return;

    this.queueUserAction((zebline) => zebline.user.setAttribute(key, value));
  }

  setAttributes(value: Partial<ZeblineUserProfileAttributes>) {
    if (value === undefined || value === null) return;

    this.queueUserAction((zebline) => zebline.user.setAttributes(value));
  }

  private queueUserAction(action: (zebline: Zebline) => void) {
    const zebline = window.zebline as Zebline | undefined;

    if (zebline?.user) {
      action(zebline);
      return;
    }

    this.pendingUserActions.push(action);
    this.ensurePolling();
  }

  private ensurePolling() {
    if (this.sdkWaitTimer) return;

    this.sdkWaitAttempts = 0;
    this.sdkWaitTimer = setInterval(() => this.flushPending(), ZeblineAdaptor.SDK_WAIT_INTERVAL_MS);
    // Try immediately too, in case everything is already ready.
    this.flushPending();
  }

  private flushPending() {
    const zebline = window.zebline as Zebline | undefined;

    if (!zebline) {
      this.sdkWaitAttempts++;
      if (this.sdkWaitAttempts >= ZeblineAdaptor.MAX_SDK_WAIT_ATTEMPTS) {
        console.warn(
          '[ZeblineAdaptor] SDK (window.zebline) did not become available after retries; dropping pending events/actions.',
        );
        this.giveUpPolling();
      }
      return;
    }

    if (this.token && this.licenceCode && !zebline.initData?.licence_code && !zebline.initData?.token) {
      zebline.init({
        token: this.token,
        licence_code: this.licenceCode,
        notificationRequest: true,
        options: {
          events: ['page_view'],
        },
      });
    }

    if (zebline.event && this.pendingEvents.length) {
      const pendingEvents = this.pendingEvents.splice(0);
      for (const event of pendingEvents) {
        zebline.event.track(event.name, this.prepareSendParams(event.name, event.params));
      }
    }

    if (zebline.user && this.pendingUserActions.length) {
      const pendingUserActions = this.pendingUserActions.splice(0);
      for (const action of pendingUserActions) {
        action(zebline);
      }
    }

    // window.zebline exists, so this counts as a "successful" check even if
    // .event/.user aren't ready yet; only bail out on missing pieces once
    // we've exhausted retries too.
    if (!zebline.event || !zebline.user) {
      this.sdkWaitAttempts++;
      if (this.sdkWaitAttempts >= ZeblineAdaptor.MAX_SDK_WAIT_ATTEMPTS) {
        console.warn(
          '[ZeblineAdaptor] zebline.event/zebline.user did not become available after retries; dropping remaining pending events/actions.',
        );
        this.giveUpPolling();
      }
      return;
    }

    this.stopPollingIfDone();
  }

  private stopPollingIfDone() {
    const nothingPending = this.pendingEvents.length === 0 && this.pendingUserActions.length === 0;

    if (nothingPending && this.sdkWaitTimer) {
      clearInterval(this.sdkWaitTimer);
      this.sdkWaitTimer = undefined;
      this.sdkWaitAttempts = 0;
    }
  }

  private giveUpPolling() {
    if (this.sdkWaitTimer) {
      clearInterval(this.sdkWaitTimer);
      this.sdkWaitTimer = undefined;
    }
    this.pendingEvents = [];
    this.pendingUserActions = [];
    this.sdkWaitAttempts = 0;
  }
}
